// src/pages/ListPage.tsx
// Individual list view page - view and manage items in a list
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  IonIcon,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
} from '@ionic/react';
import {
  chevronBack,
  add,
  createOutline,
  trashOutline,
  checkmarkDoneOutline,
  settingsOutline,
  pinOutline,
  shareOutline,
} from 'ionicons/icons';
import { supabase } from '../services/supabase';
import { downloadAndStoreIcon } from '../services/avatar';
import { useListItems, useTaskLists, useFamilyMembers } from '../hooks';
import { useAuth, useHouseholdId } from '../contexts/AuthContext';
import type { TaskList, ListItem, ListType, ListIconType } from '../types';

// UI Components
import { PageShell } from '../components/ui/PageShell';
import { Container, Stack, Row } from '../components/ui/Layout';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';
import { Modal, ModalSection } from '../components/ui/Modal';
import { IconPickerModal, IconDisplay, type IconValue } from '../components/ui/IconPicker';

import './ListPage.css';

// List type configuration
const LIST_TYPE_CONFIG: Record<ListType, { label: string; icon: string; color: string }> = {
  todo: { label: 'To-Do', icon: '✅', color: 'var(--ion-color-primary)' },
  shopping: { label: 'Shopping', icon: '🛒', color: 'var(--ion-color-success)' },
  project: { label: 'Project', icon: '📋', color: 'var(--ion-color-tertiary)' },
  checklist: { label: 'Checklist', icon: '📝', color: 'var(--ion-color-warning)' },
};

interface ListPageParams {
  listId: string;
}

const ListPage: React.FC = () => {
  const { listId } = useParams<ListPageParams>();
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const householdId = useHouseholdId();

  // Get current family member (linked to auth user)
  const { members: familyMembers } = useFamilyMembers();
  const currentMember = useMemo(() => {
    if (!user?.id || !familyMembers.length) return null;
    return familyMembers.find(m => m.user_id === user.id) || null;
  }, [user?.id, familyMembers]);

  // List data
  const [list, setList] = useState<TaskList | null>(null);
  const [listLoading, setListLoading] = useState(true);

  // Items hook
  const {
    items,
    loading: itemsLoading,
    addItem,
    update: updateItem,
    check,
    remove,
    clearChecked,
    refetch: refetchItems,
  } = useListItems({ listId });

  // Task lists hook for update/delete
  const { update: updateList, remove: deleteList } = useTaskLists({ autoFetch: false });

  // New item input
  const [newItemName, setNewItemName] = useState('');
  const [addingItem, setAddingItem] = useState(false);

  // Inline edit state
  const [inlineEditId, setInlineEditId] = useState<string | null>(null);
  const [inlineEditValue, setInlineEditValue] = useState('');
  const inlineInputRef = useRef<HTMLInputElement>(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<{
    name: string;
    iconValue: IconValue;
    description: string;
  }>({
    name: '',
    iconValue: { type: 'emoji', name: '📋' },
    description: '',
  });
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Handle icon file upload to Supabase Storage (avatars bucket)
  const handleIconUpload = async (file: File): Promise<string> => {
    if (!householdId) {
      throw new Error('No household ID');
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
    const fileName = `${householdId}/list-icons/${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('avatars')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error('Failed to upload icon:', error);
      throw error;
    }

    const { data: urlData } = supabase.storage
      .from('avatars')
      .getPublicUrl(data.path);

    return urlData.publicUrl;
  };

  // Check URL params for create mode
  const searchParams = new URLSearchParams(location.search);
  const isCreateMode = listId === 'new';
  const createType = searchParams.get('type') as ListType | null;

  // Fetch list data
  const fetchList = useCallback(async () => {
    if (isCreateMode || !listId) {
      setListLoading(false);
      return;
    }

    setListLoading(true);
    const { data, error } = await supabase
      .from('task_lists')
      .select('*, owner:family_members!owner_id(*)')
      .eq('id', listId)
      .single();

    if (error) {
      console.error('Error fetching list:', error);
      history.replace('/tasks');
    } else {
      setList(data);
      // Convert stored icon to IconValue
      const iconValue: IconValue = data.icon_type && data.icon_type !== 'emoji'
        ? {
            type: data.icon_type as IconValue['type'],
            name: data.icon || '',
            imageUrl: data.icon_url,
          }
        : { type: 'emoji', name: data.icon || '📋' };

      setEditForm({
        name: data.name,
        iconValue,
        description: data.description || '',
      });
    }
    setListLoading(false);
  }, [listId, isCreateMode, history]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Handle pull to refresh
  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([fetchList(), refetchItems()]);
    event.detail.complete();
  };

  // Handle add item
  const handleAddItem = async () => {
    if (!newItemName.trim() || addingItem) return;

    setAddingItem(true);
    try {
      await addItem(newItemName.trim());
      setNewItemName('');
    } finally {
      setAddingItem(false);
    }
  };

  // Handle Enter key in input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newItemName.trim() && !addingItem) {
      handleAddItem();
    }
  };

  // Handle check item
  const handleCheck = async (item: ListItem) => {
    // Pass the family member ID (not auth user ID) for checked_by foreign key
    await check(item.id, !item.is_checked, currentMember?.id);
  };

  // Handle delete item
  const handleDeleteItem = async (itemId: string) => {
    await remove(itemId);
  };

  // Handle clear checked
  const handleClearChecked = async () => {
    await clearChecked();
  };

  // Start inline editing
  const startInlineEdit = (item: ListItem) => {
    setInlineEditId(item.id);
    setInlineEditValue(item.name);
    // Focus input after state update
    setTimeout(() => inlineInputRef.current?.focus(), 0);
  };

  // Save inline edit
  const saveInlineEdit = async () => {
    if (!inlineEditId || !inlineEditValue.trim()) {
      setInlineEditId(null);
      return;
    }

    await updateItem(inlineEditId, { name: inlineEditValue.trim() });
    setInlineEditId(null);
    setInlineEditValue('');
  };

  // Cancel inline edit
  const cancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineEditValue('');
  };

  // Handle inline edit key events
  const handleInlineKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveInlineEdit();
    } else if (e.key === 'Escape') {
      cancelInlineEdit();
    }
  };

  // Helper to prepare icon data - downloads URL icons to our bucket
  const prepareIconData = async (iconValue: IconValue) => {
    if (iconValue.type === 'emoji') {
      return {
        icon: iconValue.name,
        icon_type: null as ListIconType | null,
        icon_url: null as string | null,
        icon_source_url: null as string | null,
      };
    }

    // For URL-type icons, download and store in our bucket
    if (iconValue.type === 'url' && iconValue.imageUrl && householdId) {
      console.log('Downloading URL icon to bucket...');
      const result = await downloadAndStoreIcon(householdId, iconValue.imageUrl, 'list-icons');

      if (result) {
        return {
          icon: iconValue.name || 'url-icon',
          icon_type: 'url' as ListIconType,
          icon_url: result.storedUrl, // Served from our bucket
          icon_source_url: result.sourceUrl, // Original URL for reference
        };
      }
      // If download fails, fall through to use original URL
      console.warn('Failed to download URL icon, using original URL');
    }

    // For upload and other types, use as-is
    return {
      icon: iconValue.name,
      icon_type: iconValue.type as ListIconType,
      icon_url: iconValue.imageUrl || null,
      icon_source_url: null as string | null,
    };
  };

  // Handle create list
  const handleCreateList = async () => {
    if (!householdId || !editForm.name.trim()) return;

    setSaving(true);
    try {
      // Prepare icon data (may download URL icons)
      const iconData = await prepareIconData(editForm.iconValue);

      const { data, error } = await supabase
        .from('task_lists')
        .insert({
          household_id: householdId,
          name: editForm.name.trim(),
          ...iconData,
          description: editForm.description.trim() || null,
          list_type: createType || 'todo',
        })
        .select()
        .single();

      if (error) throw error;

      // Navigate to the new list
      history.replace(`/lists/${data.id}`);
    } catch (err) {
      console.error('Failed to create list:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle update list
  const handleUpdateList = async () => {
    if (!list || !editForm.name.trim()) return;

    setSaving(true);
    try {
      // Prepare icon data (may download URL icons)
      const iconData = await prepareIconData(editForm.iconValue);

      // Use direct Supabase update to properly handle null values
      // (The hook's update function may strip undefined values)
      const { error } = await supabase
        .from('task_lists')
        .update({
          name: editForm.name.trim(),
          icon: iconData.icon,
          icon_type: iconData.icon_type, // null will clear the field
          icon_url: iconData.icon_url, // null will clear the field
          icon_source_url: iconData.icon_source_url, // null will clear the field
          description: editForm.description.trim() || null,
        })
        .eq('id', list.id);

      if (error) throw error;

      await fetchList();
      setShowEditModal(false);
    } catch (err) {
      console.error('Failed to update list:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle delete list
  const handleDeleteList = async () => {
    if (!list) return;

    setSaving(true);
    try {
      await deleteList(list.id, true); // Delete items too
      history.replace('/tasks');
    } catch (err) {
      console.error('Failed to delete list:', err);
    } finally {
      setSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle toggle pin
  const handleTogglePin = async () => {
    if (!list) return;
    await updateList(list.id, { is_pinned: !list.is_pinned });
    await fetchList();
  };

  // Stats
  const stats = useMemo(() => {
    const total = items.length;
    const checked = items.filter(i => i.is_checked).length;
    const unchecked = total - checked;
    return { total, checked, unchecked };
  }, [items]);

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: Record<string, ListItem[]> = {};
    const uncategorized: ListItem[] = [];

    // Sort: unchecked first, then by sort_order
    const sortedItems = [...items].sort((a, b) => {
      if (a.is_checked !== b.is_checked) {
        return a.is_checked ? 1 : -1;
      }
      return (a.sort_order || 0) - (b.sort_order || 0);
    });

    sortedItems.forEach(item => {
      if (item.category) {
        if (!groups[item.category]) {
          groups[item.category] = [];
        }
        groups[item.category].push(item);
      } else {
        uncategorized.push(item);
      }
    });

    const result: Array<{ category: string; items: ListItem[] }> = [];

    // Add categorized groups alphabetically
    Object.keys(groups)
      .sort()
      .forEach(category => {
        result.push({ category, items: groups[category] });
      });

    // Add uncategorized at the end
    if (uncategorized.length > 0) {
      result.push({ category: '', items: uncategorized });
    }

    return result;
  }, [items]);

  // Loading state
  if (listLoading) {
    return (
      <PageShell title="Loading..." headerType="page" noPadding>
        <Container size="lg" padding="md">
          <div className="list-page__loading">
            <IonSpinner name="crescent" />
          </div>
        </Container>
      </PageShell>
    );
  }

  // Helper to render icon from IconValue
  const renderIconValue = (iconValue: IconValue, size = 32) => {
    if (iconValue.type === 'emoji') {
      return <span style={{ fontSize: size }}>{iconValue.name}</span>;
    }
    return <IconDisplay icon={iconValue} size={size} />;
  };

  // Create mode
  if (isCreateMode) {
    const typeConfig = createType ? LIST_TYPE_CONFIG[createType] : LIST_TYPE_CONFIG.todo;

    return (
      <PageShell
        title="New List"
        headerType="page"
        noPadding
        showBack
      >
        <Container size="lg" padding="md">
          <Stack gap="lg">
            <div className="list-page__create-header">
              <button
                className="list-page__icon-btn"
                onClick={() => setShowIconPicker(true)}
              >
                {renderIconValue(editForm.iconValue, 32)}
              </button>
              <TextInput
                label="List Name"
                value={editForm.name}
                onChange={(v) => setEditForm(f => ({ ...f, name: v }))}
                placeholder={`My ${typeConfig.label} List`}
                variant="default"
                size="lg"
              />
            </div>

            <TextInput
              label="Description (optional)"
              value={editForm.description}
              onChange={(v) => setEditForm(f => ({ ...f, description: v }))}
              placeholder="Add a description..."
              variant="default"
            />

            <Row gap="md">
              <Button
                variant="primary"
                fill="outline"
                onClick={() => history.goBack()}
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateList}
                disabled={!editForm.name.trim() || saving}
                style={{ flex: 1 }}
              >
                {saving ? 'Creating...' : 'Create List'}
              </Button>
            </Row>
          </Stack>
        </Container>

        {/* Icon Picker */}
        <IconPickerModal
          isOpen={showIconPicker}
          onClose={() => setShowIconPicker(false)}
          onSave={(icon) => {
            setEditForm(f => ({ ...f, iconValue: icon }));
            setShowIconPicker(false);
          }}
          currentIcon={editForm.iconValue}
          title="Choose List Icon"
          onFileUpload={handleIconUpload}
          maxFileSize={2 * 1024 * 1024}
        />
      </PageShell>
    );
  }

  // List not found
  if (!list) {
    return (
      <PageShell title="List Not Found" headerType="page" noPadding showBack>
        <Container size="lg" padding="md">
          <div className="list-page__empty">
            <span className="list-page__empty-icon">🔍</span>
            <p>This list doesn't exist or was deleted.</p>
            <Button variant="primary" onClick={() => history.replace('/tasks')}>
              Back to Tasks
            </Button>
          </div>
        </Container>
      </PageShell>
    );
  }

  const typeConfig = LIST_TYPE_CONFIG[list.list_type] || LIST_TYPE_CONFIG.todo;

  return (
    <PageShell
      title={list.name}
      headerType="page"
      noPadding
      showBack
    >
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <Container size="lg" padding="md">
        <Stack gap="lg">
          {/* List Header */}
          <div className="list-page__header">
            <div className="list-page__header-info">
              <span className="list-page__header-icon">
                {list.icon_type && list.icon_type !== 'emoji' && list.icon_url ? (
                  <IconDisplay
                    icon={{ type: list.icon_type, name: list.icon || '', imageUrl: list.icon_url }}
                    size={40}
                  />
                ) : (
                  list.icon || typeConfig.icon
                )}
              </span>
              <div>
                <h1 className="list-page__header-title">{list.name}</h1>
                <div className="list-page__header-meta">
                  <span className="list-page__header-type">{typeConfig.label}</span>
                  {list.is_pinned && (
                    <span className="list-page__header-pinned">
                      <IonIcon icon={pinOutline} /> Pinned
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="list-page__header-actions">
              <button
                className="list-page__action-btn"
                onClick={handleTogglePin}
                aria-label={list.is_pinned ? 'Unpin list' : 'Pin list'}
              >
                <IonIcon icon={pinOutline} />
              </button>
              <button
                className="list-page__action-btn"
                onClick={() => setShowEditModal(true)}
                aria-label="Edit list"
              >
                <IonIcon icon={createOutline} />
              </button>
            </div>
          </div>

          {/* Stats */}
          {stats.total > 0 && (
            <div className="list-page__stats">
              <span className="list-page__stat">
                {stats.unchecked} remaining
              </span>
              {stats.checked > 0 && (
                <>
                  <span className="list-page__stat list-page__stat--muted">
                    {stats.checked} done
                  </span>
                  <button
                    className="list-page__clear-btn"
                    onClick={handleClearChecked}
                  >
                    <IonIcon icon={checkmarkDoneOutline} />
                    Clear done
                  </button>
                </>
              )}
            </div>
          )}

          {/* Add Item Input */}
          <div className="list-page__add-item">
            <IonIcon icon={add} className="list-page__add-icon" />
            <input
              type="text"
              className="list-page__add-input"
              placeholder="Add an item..."
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={addingItem}
            />
            {newItemName.trim() && (
              <button
                className="list-page__add-btn"
                onClick={handleAddItem}
                disabled={addingItem}
              >
                {addingItem ? <IonSpinner name="crescent" /> : <IonIcon icon={add} />}
              </button>
            )}
          </div>

          {/* Items List */}
          {itemsLoading ? (
            <div className="list-page__loading">
              <IonSpinner name="crescent" />
            </div>
          ) : items.length === 0 ? (
            <div className="list-page__empty">
              <span className="list-page__empty-icon">📝</span>
              <p>No items yet</p>
              <p className="list-page__empty-hint">Add your first item above</p>
            </div>
          ) : (
            <div className="list-page__items">
              {groupedItems.map(group => (
                <div key={group.category || '_uncategorized'} className="list-page__group">
                  {group.category && (
                    <div className="list-page__category">{group.category}</div>
                  )}
                  {group.items.map(item => (
                    <div
                      key={item.id}
                      className={`list-page__item ${item.is_checked ? 'checked' : ''}`}
                    >
                      <button
                        className={`list-page__check-btn ${item.is_checked ? 'checked' : ''}`}
                        onClick={() => handleCheck(item)}
                        aria-label={item.is_checked ? 'Uncheck item' : 'Check item'}
                      >
                        <span className="list-page__check-icon" />
                      </button>

                      <div className="list-page__item-content">
                        {inlineEditId === item.id ? (
                          <input
                            ref={inlineInputRef}
                            type="text"
                            className="list-page__inline-input"
                            value={inlineEditValue}
                            onChange={(e) => setInlineEditValue(e.target.value)}
                            onKeyDown={handleInlineKeyDown}
                            onBlur={saveInlineEdit}
                            autoFocus
                          />
                        ) : (
                          <span
                            className="list-page__item-name"
                            onClick={() => startInlineEdit(item)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && startInlineEdit(item)}
                          >
                            {item.name}
                          </span>
                        )}
                        {item.quantity && (
                          <span className="list-page__item-qty">
                            {item.quantity} {item.unit || ''}
                          </span>
                        )}
                        {item.notes && (
                          <span className="list-page__item-notes">{item.notes}</span>
                        )}
                      </div>

                      <button
                        className="list-page__delete-btn"
                        onClick={() => handleDeleteItem(item.id)}
                        aria-label="Delete item"
                      >
                        <IonIcon icon={trashOutline} />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Stack>
      </Container>

      {/* Edit List Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        size="md"
        header={{ title: 'Edit List' }}
        footer={{
          primaryAction: {
            label: saving ? 'Saving...' : 'Save',
            onClick: handleUpdateList,
            disabled: !editForm.name.trim() || saving,
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: () => setShowEditModal(false),
          },
        }}
      >
        <ModalSection spacing="normal">
          <Stack gap="md">
            <Row gap="md" align="end">
              <button
                className="list-page__icon-btn"
                onClick={() => setShowIconPicker(true)}
                style={{ marginBottom: 4 }}
              >
                {renderIconValue(editForm.iconValue, 32)}
              </button>
              <div style={{ flex: 1 }}>
                <TextInput
                  label="List Name"
                  value={editForm.name}
                  onChange={(v) => setEditForm(f => ({ ...f, name: v }))}
                  placeholder="List name"
                  variant="default"
                />
              </div>
            </Row>

            <TextInput
              label="Description"
              value={editForm.description}
              onChange={(v) => setEditForm(f => ({ ...f, description: v }))}
              placeholder="Add a description..."
              variant="default"
            />

            <div style={{ paddingTop: 16, borderTop: '1px solid var(--ion-border-color)' }}>
              <Button
                variant="danger"
                fill="outline"
                size="small"
                onClick={() => setShowDeleteConfirm(true)}
                style={{ width: '100%' }}
              >
                <IonIcon icon={trashOutline} slot="start" />
                Delete List
              </Button>
            </div>
          </Stack>
        </ModalSection>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        size="sm"
        header={{ title: 'Delete List?' }}
        footer={{
          primaryAction: {
            label: saving ? 'Deleting...' : 'Delete',
            onClick: handleDeleteList,
            disabled: saving,
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: () => setShowDeleteConfirm(false),
          },
        }}
      >
        <ModalSection spacing="normal">
          <p style={{ margin: 0, color: 'var(--ion-color-medium)' }}>
            Are you sure you want to delete "{list.name}"? This will also delete all {items.length} items in the list.
          </p>
        </ModalSection>
      </Modal>

      {/* Icon Picker */}
      <IconPickerModal
        isOpen={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onSave={(icon) => {
          setEditForm(f => ({ ...f, iconValue: icon }));
          setShowIconPicker(false);
        }}
        currentIcon={editForm.iconValue}
        title="Choose List Icon"
        onFileUpload={handleIconUpload}
        maxFileSize={2 * 1024 * 1024}
      />
    </PageShell>
  );
};

export default ListPage;
